import { cn } from '../lib/cn.js';

/**
 * Avatar-gradiënten uit de blauw-familie. De ingelogde gebruiker krijgt bewust een
 * afwijkende kleur (oranje-familie) zodat "ik" herkenbaar is in lijsten en reacties.
 *
 * `--avatar-primary`, `--avatar-cyan` en `--avatar-accent` zijn de ruwe merkkleuren, ook in de
 * app-zone (waar `--brand-primary` de AA-variant is). De standaard staat sinds MRK-2 in tokens.css,
 * niet meer als terugval hier; onder `data-brand` volgen ze de kleuren van de klant.
 */
const primary = 'var(--avatar-primary)';
const gradients = [
  `linear-gradient(135deg, ${primary}, var(--brand-dark-blue))`,
  `linear-gradient(135deg, var(--avatar-cyan), var(--brand-primary-dark))`,
  `linear-gradient(135deg, var(--brand-dark-blue), ${primary})`,
  `linear-gradient(135deg, var(--brand-primary-dark), var(--avatar-cyan))`,
];
const selfGradient = 'linear-gradient(135deg, var(--avatar-accent), var(--brand-orange-deep))';

const sizeClasses = {
  sm: 'h-7 w-7 text-micro',
  md: 'h-9 w-9 text-small',
  // 44 px: de maat van de afzender op een berichtkaart (replica-spec §9.1), en tegelijk de
  // kleinste raakvlakmaat. Zo is de avatar van de afzender ook op een telefoon aan te tikken.
  lg: 'h-11 w-11 text-content',
  xl: 'h-12 w-12 text-title-md',
  // 72 px: de profielkaart van een teamlid (Stap 17, spec §2 frame 3). Groter dan elke andere
  // avatar in de app, omdat die kaart over één mens gaat en niet over een lijst.
  '2xl': 'h-[72px] w-[72px] text-page',
} as const;

export interface AvatarProps {
  name: string;
  /** De ingelogde gebruiker; krijgt de afwijkende kleur. */
  self?: boolean;
  size?: keyof typeof sizeClasses;
  className?: string;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/** Stabiele keuze uit het gradiëntenpalet, zodat dezelfde persoon altijd dezelfde kleur heeft. */
function gradientFor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) % 997;
  return gradients[hash % gradients.length] as string;
}

export function Avatar({ name, self, size = 'md', className }: AvatarProps) {
  return (
    <span
      title={name}
      data-avatar
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-pill font-heading font-semibold text-text-on-dark',
        sizeClasses[size],
        className,
      )}
      style={{ backgroundImage: self ? selfGradient : gradientFor(name) }}
    >
      <span aria-hidden>{initials(name)}</span>
      <span className="sr-only">{name}</span>
    </span>
  );
}
